import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import { useNotify } from '../../context/NotifyContext';

function optionsToText(opts) {
  if (!Array.isArray(opts)) return '';
  return opts.join('\n');
}

/** Lines from textarea → option strings (allows leading blank for “unset”) */
function textToOptionLines(text) {
  return String(text ?? '').split(/\r?\n/).map((l) => l.trim());
}

const AdminDemographicsOptionsPanel = () => {
  const { showSuccess, showError } = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendOutText, setSendOutText] = useState('');
  const [resultText, setResultText] = useState('');
  const [billingText, setBillingText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDemographicsUiOptions();
      setSendOutText(optionsToText(data.demographicsSendOutOptions));
      setResultText(optionsToText(data.demographicsResultOptions));
      setBillingText(optionsToText(data.demographicsBillingOptions));
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.message || e.message || 'Could not load demographics options');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    try {
      await adminAPI.patchDemographicsUiOptions({
        demographicsSendOutOptions: textToOptionLines(sendOutText),
        demographicsResultOptions: textToOptionLines(resultText),
        demographicsBillingOptions: textToOptionLines(billingText)
      });
      showSuccess('Demographics dropdown options saved. Labs will see these on the next page load.');
      await load();
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Loading demographics options…</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="card-title" style={{ marginBottom: '8px' }}>
        Lab demographics dropdowns
      </h2>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px', maxWidth: '720px' }}>
        Control the choices labs see for <strong>Send out</strong>, <strong>Result status</strong>, and{' '}
        <strong>Payment status</strong> on the Demographics sheet. Put one value per line; start with a blank line if you
        want an empty “—” choice. Imported PDF values that are not in the list still display until the user picks a
        listed option.
      </p>

      <div style={{ display: 'grid', gap: '16px', maxWidth: '720px' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label" htmlFor="admin-demo-sendout">
            Send out options
          </label>
          <textarea
            id="admin-demo-sendout"
            className="form-textarea"
            rows={6}
            value={sendOutText}
            onChange={(e) => setSendOutText(e.target.value)}
            spellCheck={false}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label" htmlFor="admin-demo-result">
            Result status options
          </label>
          <textarea
            id="admin-demo-result"
            className="form-textarea"
            rows={6}
            value={resultText}
            onChange={(e) => setResultText(e.target.value)}
            spellCheck={false}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label" htmlFor="admin-demo-billing">
            Payment status options
          </label>
          <textarea
            id="admin-demo-billing"
            className="form-textarea"
            rows={6}
            value={billingText}
            onChange={(e) => setBillingText(e.target.value)}
            spellCheck={false}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}
          />
        </div>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save options'}
        </button>
        <button type="button" className="btn btn-outline" disabled={saving} onClick={load}>
          Reload
        </button>
      </div>
    </div>
  );
};

export default AdminDemographicsOptionsPanel;
